import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { inr, fmtDate } from "@/lib/format";

export default function SExpenses() {
  const { settings, bizDate } = useAuth();
  const [date, setDate] = useState(bizDate);
  const [f, setF] = useState({ category: "", amount: "", paid_to: "", description: "" });
  const [rk, setRk] = useState(0);
  const { data } = useFetch("/expenses", { date_from: date, date_to: date, limit: 100 }, rk);
  const add = async () => {
    if (!f.category) return toast.error("Choose a category.");
    try {
      await api.post("/expenses", { ...f, amount: Number(f.amount), date });
      toast.success("Expense added");
      setF({ category: "", amount: "", paid_to: "", description: "" });
      setRk(rk + 1);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      setRk(rk + 1);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Expenses</h1>
        <Input type="date" value={date} max={bizDate} onChange={(e) => setDate(e.target.value)} className="h-9 w-40" data-testid="exp-date" />
      </div>
      <div className="space-y-3 rounded-md border border-slate-200 bg-white p-4" data-testid="expense-form">
        <div className="grid grid-cols-3 gap-2">
          {settings?.expense_categories.map((c) => (
            <button key={c} onClick={() => setF({ ...f, category: c })} data-testid={`exp-cat-${c.replace(/\W/g, "")}`}
              className={`rounded border px-2 py-3 text-xs font-semibold transition-colors ${f.category === c ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 text-slate-700"}`}>{c}</button>
          ))}
        </div>
        <Input type="number" inputMode="decimal" placeholder="Amount ₹" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-12 font-mono text-lg" data-testid="exp-amount" />
        <Input placeholder="Paid to (e.g. XYZ Petrol Pump)" value={f.paid_to} onChange={(e) => setF({ ...f, paid_to: e.target.value })} className="h-11" data-testid="exp-paid-to" />
        <Input placeholder="Note (optional)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="h-11" data-testid="exp-note" />
        <Button className="h-12 w-full" disabled={!f.amount} onClick={add} data-testid="exp-add-button">Add expense</Button>
      </div>
      <div>
        <div className="mb-2 flex justify-between text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">
          <span>{fmtDate(date)}</span><span data-testid="exp-total">Total {inr(data?.sum || 0)}</span>
        </div>
        <div className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white">
          {(data?.items || []).map((e) => (
            <div key={e.id} className="flex items-center justify-between px-4 py-3" data-testid={`exp-row-${e.expense_no}`}>
              <div><p className="text-sm font-semibold">{e.category} · <span className="font-mono">{inr(e.amount)}</span></p><p className="text-xs text-slate-500">{e.paid_to}{e.description && ` · ${e.description}`}</p></div>
              <Button size="icon" variant="ghost" onClick={() => del(e.id)} data-testid={`exp-delete-${e.expense_no}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
            </div>
          ))}
          {data?.items?.length === 0 && <p className="px-4 py-3 text-sm text-slate-500">No expenses for this day.</p>}
        </div>
      </div>
    </div>
  );
}
